import { useCallback } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { parseISO } from 'date-fns';

import { useSchoolInfo } from './useSchoolInfo';
import { type ClassRecording } from '../components/classroom/types';
import { filterRecordingsBySessions } from '../components/classroom/util';
import { LEGACY_RECORDINGS_LIST, RECORDINGS_LIST } from '../config/apiPaths';
import { type ClassSessionInfo, type ClassStudentGroup } from '../context/types';
import { getUserUuidHeaders } from '../utils/httpUtils';
import { processApiPath } from '../utils/url';

type UseRecordingsT = {
    userUuid: string;
    showLegacyRecordings: boolean;
    doFetch: boolean;
    isPrivilegedUser: boolean;
    classUuid?: string;
    meetingId?: string;
    previousSessions?: ClassSessionInfo[];
    upcomingSessions?: ClassSessionInfo[];
    restrictRecordingsByGroups?: boolean;
    classGroups?: ClassStudentGroup[];
};

export const useRecordings = (props: UseRecordingsT) => {
    const {
        userUuid,
        classUuid,
        meetingId,
        previousSessions,
        upcomingSessions,
        isPrivilegedUser,
        restrictRecordingsByGroups,
        classGroups,
        showLegacyRecordings,
        doFetch
    } = props;

    const { data: school, loading: isLoadingSchoolInfo } = useSchoolInfo();

    const schoolUuid = school?.uuid as string;

    const filterRecordingsByGroupAccess = useCallback(
        (rawRecordings: ClassRecording[]): ClassRecording[] => {
            const sessions = [...(previousSessions ?? []), ...(upcomingSessions ?? [])];

            // Build a list of recordings filtered to only include those where the start and end times of the recordings
            // fall within a session times.
            // Note previousSessions/upcomingSessions will already have been filtered to only include sessions
            // applicable to the user by group membership.
            return filterRecordingsBySessions(rawRecordings, sessions);
        },
        [previousSessions, upcomingSessions]
    );

    const fetchLegacyRecordingData = useCallback(async (): Promise<ClassRecording[]> => {
        if (!meetingId) {
            return [];
        }

        try {
            const url = processApiPath(LEGACY_RECORDINGS_LIST, { meetingId });
            const { data } = await axios.get(url, { headers: getUserUuidHeaders(userUuid) });

            return data;
        } catch (e) {
            console.error(e);

            return [];
        }
    }, [meetingId, userUuid]);

    const fetchRecordingData = useCallback(async (): Promise<ClassRecording[]> => {
        if (!schoolUuid || !classUuid) {
            return [];
        }

        try {
            const url = processApiPath(RECORDINGS_LIST, { schoolUuid: schoolUuid, classUuid });
            const { data } = await axios.get(url, { headers: getUserUuidHeaders(userUuid) });
            console.log('Got Recordings', data);
            // Sort recordings.
            const recs: ClassRecording[] = data.recordings.sort((a: ClassRecording, b: ClassRecording) => {
                if (parseISO(a.when) > parseISO(b.when)) {
                    return -1;
                }
                if (parseISO(a.when) < parseISO(b.when)) {
                    return 1;
                }
                return 0;
            });

            if (isPrivilegedUser || !classGroups?.length || !restrictRecordingsByGroups) {
                // If no restrictions on sessions/recordings, or the user is an instructor/assistant
                // or if there are no groups then all recordings should show.
                return recs;
            }

            // Filter the recordings to only show ones where a user is a member of a group with matching session times.
            const sortedRecs = filterRecordingsByGroupAccess(recs);
            return sortedRecs;
        } catch (e) {
            console.error(e);
            return [];
        }
    }, [
        restrictRecordingsByGroups,
        classGroups?.length,
        classUuid,
        filterRecordingsByGroupAccess,
        isPrivilegedUser,
        schoolUuid,
        userUuid
    ]);

    const queryKey = [RECORDINGS_LIST, classUuid];

    const {
        isLoading: isLoadingRecordings,
        isFetching: isFetchingRecordings,
        refetch: refetchRecordings,
        data
    } = useQuery({
        enabled: !!schoolUuid && !!classUuid && !!meetingId && doFetch,
        queryKey,
        refetchInterval: 15000,
        cacheTime: 15000,
        staleTime: 15000,
        refetchOnWindowFocus: true,
        refetchOnMount: false,
        queryFn: async () => await (showLegacyRecordings ? fetchLegacyRecordingData : fetchRecordingData)()
    });

    return {
        data,
        isLoadingRecordings,
        isFetchingRecordings,
        refetchRecordings
    };
};
